import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// API base URL: Use Render URL until DNS is configured for custom domain
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

export const useSubscription = () => {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) {
        setHasActiveSubscription(false);
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently({ audience: 'https://api.eternavaults.com' });

        const response = await fetch(`${API_BASE_URL}/api/subscription/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setHasActiveSubscription(data.active);
          setSubscriptionData(data);
          setIsRateLimited(false);
        } else if (response.status === 429) {
          // Rate limit hit - don't treat as "no subscription"
          console.warn('Rate limit hit on subscription check');
          setIsRateLimited(true);
          // Keep previous subscription state to avoid incorrect redirects
        } else {
          setHasActiveSubscription(false);
          setSubscriptionData(null);
          setIsRateLimited(false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // Check for rate limit indicators in fetch errors (CORS preflight failures)
        const errorMessage = error?.message || error?.toString() || '';
        if (
          /too many requests|rate limit|429/i.test(errorMessage) ||
          /failed to fetch/i.test(errorMessage)
        ) {
          console.warn('Rate limit or network error detected in subscription check');
          setIsRateLimited(true);
        } else {
          setHasActiveSubscription(false);
          setIsRateLimited(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, getAccessTokenSilently]);

  return { hasActiveSubscription, isLoading, subscriptionData, isRateLimited };
};
